import { type ReactNode } from 'react';
import BankingImg from 'assets/images/banking.svg?react';
import { Form, Field } from 'formik';
import { Row, Col } from 'react-flexbox-grid';
import { useIntl } from 'react-intl';
import ScrollToError from 'components/common/ScrollToError';
import NumberInput from 'components/inputs/NumberInput';
import TextInput from 'components/inputs/TextInput';
import SliderWithTooltip from 'components/inputs/SliderWithTooltip';
import ButtonSelect from 'components/inputs/ButtonSelect';
import DistrictHeatingStandard from 'assets/icons/co2-calculator/heating-system/district-heating-standard.svg?react';
import NaturalGas from 'assets/icons/co2-calculator/heating-system/natural-gas.svg?react';
import Electric from 'assets/icons/co2-calculator/heating-system/electric.svg?react';
import Oil from 'assets/icons/co2-calculator/heating-system/oil.svg?react';
import Coal from 'assets/icons/co2-calculator/heating-system/coal.svg?react';
import HeatingPump from 'assets/icons/co2-calculator/heating-system/heating-pump.svg?react';
import WoodPellets from 'assets/icons/co2-calculator/heating-system/wood-pellets.svg?react';
import Switch from 'components/inputs/Switch';
import s from '../../calculators.module.scss';
import type { IOptionIconArgs } from '../types';

export interface IBuyingHouseEstimationFormProps {
  hasAdditionalFeatures?: boolean;
  buttons?: ReactNode;
}

const BuyingHouseEstimationForm = ({
  hasAdditionalFeatures,
  buttons,
}: IBuyingHouseEstimationFormProps) => {
  const { formatMessage: t } = useIntl();

  return (
    <Row between="lg" center="md">
      <Col md={9} lg={6}>
        <Form>
          <ScrollToError />
          <Field
            name="construction_year"
            component={NumberInput}
            label={t({ id: 'buying_house_wizard.construction_year' })}
            thousandSeparator=""
            format="####"
          />
          <Field
            name="full_address"
            component={TextInput}
            label={t({ id: 'buying_house_wizard.full_address' })}
          />

          <Field name="zip" component={TextInput} label={t({ id: 'project_wizard.zip_code' })} />

          <Field
            name="land_area"
            component={SliderWithTooltip}
            min={200}
            step={50}
            max={3000}
            units="m²"
            label={t({ id: 'buying_house_wizard.land_area' })}
          />
          <Field
            name="house_area"
            component={SliderWithTooltip}
            step={5}
            min={25}
            max={300}
            units="m²"
            label={t({ id: 'buying_house_wizard.house_area' })}
          />
          <Field
            name="house_category"
            component={ButtonSelect}
            options={[
              {
                label: t({ id: 'buying_house_wizard.category.detached_house' }),
                value: 'detached_house',
              },
              {
                label: t({ id: 'buying_house_wizard.category.townhouse' }),
                value: 'townhouse',
              },
              {
                label: t({ id: 'buying_house_wizard.category.semi_detached_house' }),
                value: 'semi_detached_house',
              },
            ]}
            label={t({ id: 'buying_house_wizard.category.name' })}
          />
          <Field
            name="basement_type"
            component={ButtonSelect}
            options={[
              {
                label: t({ id: 'buying_house_wizard.basement.full_basement' }),
                value: 'full_basement',
              },
              {
                label: t({ id: 'buying_house_wizard.basement.partially_basement' }),
                value: 'partially_basement',
              },
              {
                label: t({ id: 'buying_house_wizard.basement.no_basement' }),
                value: 'no_basement',
              },
            ]}
            label={t({ id: 'buying_house_wizard.basement.name' })}
          />
          <Field
            name="parking"
            component={ButtonSelect}
            options={[
              {
                label: t({ id: 'buying_house_wizard.parking.garage' }),
                value: 'garage',
              },
              {
                label: t({ id: 'buying_house_wizard.parking.carport' }),
                value: 'carport',
              },
              {
                label: t({ id: 'buying_house_wizard.parking.none' }),
                value: 'none',
              },
              {
                label: t({ id: 'buying_house_wizard.parking.outdoor_parking_space' }),
                value: 'outdoor_parking_space',
              },
            ]}
            label={t({ id: 'buying_house_wizard.parking.name' })}
          />

          <Field
            name="heating_system"
            component={ButtonSelect}
            inOneRow={false}
            options={[
              {
                topIcon: ({ altText }: IOptionIconArgs) => (
                  <DistrictHeatingStandard className="fill" title={altText} />
                ),
                label: t({ id: 'buying_house_wizard.heating_system.district' }),
                value: 'district',
              },
              {
                topIcon: ({ altText }: IOptionIconArgs) => (
                  <NaturalGas className="fill" title={altText} />
                ),
                label: t({ id: 'buying_house_wizard.heating_system.gas' }),
                value: 'gas',
              },
              {
                topIcon: ({ altText }: IOptionIconArgs) => <Oil className="fill" title={altText} />,
                label: t({ id: 'buying_house_wizard.heating_system.oil' }),
                value: 'oil',
              },
              {
                topIcon: ({ altText }: IOptionIconArgs) => (
                  <WoodPellets className="fill" title={altText} />
                ),
                label: t({ id: 'buying_house_wizard.heating_system.pellet' }),
                value: 'pellet',
              },
              {
                topIcon: ({ altText }: IOptionIconArgs) => (
                  <HeatingPump className="fill" title={altText} />
                ),
                label: t({ id: 'buying_house_wizard.heating_system.heat_pump' }),
                value: 'heat_pump',
              },
              {
                topIcon: ({ altText }: IOptionIconArgs) => (
                  <Coal className="fill" title={altText} />
                ),
                label: t({ id: 'buying_house_wizard.heating_system.wood_or_coal' }),
                value: 'wood_or_coal',
              },
              {
                topIcon: ({ altText }: IOptionIconArgs) => (
                  <Electric className="fill" title={altText} />
                ),
                label: t({ id: 'buying_house_wizard.heating_system.electric' }),
                value: 'electric',
              },
            ]}
            label={t({ id: 'buying_house_wizard.heating_system.name' })}
          />
          {!hasAdditionalFeatures ? (
            <>
              <h2>{t({ id: 'buying_house_wizard.renovations_or_additional_investments' })}</h2>
              <Field
                name="additional_features"
                component={Switch}
                label={t({ id: 'project_wizard.has_additional_features' })}
              />
            </>
          ) : null}

          {buttons}
        </Form>
      </Col>

      <Col md={8} lg={6} first="xs" last="lg">
        <div className={s.imgBox}>
          <BankingImg
            width="90%"
            title={t({ id: 'alt_text.project_details_page.buying_house.second_step' })}
          />
        </div>
      </Col>
    </Row>
  );
};

export default BuyingHouseEstimationForm;
